import { DocumentSource } from '@/api/preFlight/enumerations';
import {
  IconAttachFileMS,
  IconDeleteMS,
  IconDownloadMS,
  IconEditNoteMS,
  IconFolderOpenMS,
} from '@/constants/icons';
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import { ChangeEvent, useState } from 'react';

import { convertRegistrationUf } from '../../../../api/enumerations';
import {
  ApprovePreFlightParams,
  PreFlightData,
  PreFlightDocumentData,
  RejectPreFlightParams,
  UpdateWorkOrderParams,
} from '../../../../api/preFlight/types';
import {
  CustomFormControl,
  CustomTextField,
  SelectTextField,
  SelectTextFieldString,
} from '../../../../components/CustomInput';
import StandardMap from '../../../../components/Maps/StandardMap';
import MapSearchButton from '../../../../components/MapSearchButton';
import {
  selectPreFlightAreaType,
  selectPreFlightArtRrt,
  selectPreFlightMethod,
  selectPreFlightModality,
  selectPreFlightObjective,
  selectPreFlightPropertyType,
  selectRegistrationUf,
} from '../../../../constants/selectOptions';
import { Constants } from '../../constants';
import { DeedUpload } from './DeedUpload';
import { StaticView } from './StaticView';
import {
  AttachFileButon,
  AttachmentsBox,
  CancelFileButton,
  DialogTitle,
  DocumentBox,
  EditButton,
  FileContainer,
  FileList,
  FileNameTypography,
  InputBox,
  SaveButton,
  SectionTitle,
  StyledIcon,
  SubmitFileBox,
  SubmitFileButton,
} from './styles';
import { usePreFlightDetail } from './usePreFlightDetail';

interface PreFlightDetailProps {
  selectedPreflight: PreFlightData;
  handleUpdateWorkOrder: (
    id: number,
    params: UpdateWorkOrderParams
  ) => Promise<boolean>;
  loadingUpdateWorkOrder: boolean;
  handleApprove: (id: number, params?: ApprovePreFlightParams) => Promise<void>;
  handleReject: (id: number, params: RejectPreFlightParams) => Promise<void>;
  loadingApprove: boolean;
  loadingReject: boolean;
  onDeedUpload: (doc: PreFlightDocumentData) => void;
  onDocumentAdded: (doc: PreFlightDocumentData) => void;
  onDocumentDeleted: (reference: string) => void;
}

export function PreFlightDetail({
  selectedPreflight,
  handleUpdateWorkOrder,
  loadingUpdateWorkOrder,
  handleApprove,
  handleReject,
  loadingApprove,
  loadingReject,
  onDeedUpload,
  onDocumentAdded,
  onDocumentDeleted,
}: PreFlightDetailProps): JSX.Element {
  const {
    isEditing,
    setIsEditing,
    matriculaDoc,
    requesterName,
    setRequesterName,
    requesterPhone,
    setRequesterPhone,
    requesterEmail,
    setRequesterEmail,
    objective,
    setObjective,
    propertyType,
    setPropertyType,
    areaType,
    setAreaType,
    modality,
    setModality,
    method,
    setMethod,
    street,
    setStreet,
    addressNumber,
    setAddressNumber,
    complement,
    setComplement,
    zipCode,
    setZipCode,
    district,
    setDistrict,
    city,
    setCity,
    addressState,
    setAddressState,
    deedNumber,
    setDeedNumber,
    artRrt,
    setArtRrt,
    technicalReview,
    setTechnicalReview,
    updatedDeed,
    setUpdatedDeed,
    companionName,
    setCompanionName,
    companionEmail,
    setCompanionEmail,
    companionPhone,
    setCompanionPhone,
    description,
    setDescription,
    fileName,
    setFileName,
    pinPlace,
    setPinPlace,
    isAddressEdited,
    setIsAddressEdited,
    hasSearched,
    setHasSearched,
    resetForm,
    handleSave,
    downloadFile,
    handleReset,
    handleDeleteFile,
    handleFileUpload,
    handleSubmitFile,
  } = usePreFlightDetail({
    selectedPreflight,
    handleUpdateWorkOrder,
    onDocumentAdded,
    onDocumentDeleted,
  });

  const [searchMap, setSearchMap] = useState(false);

  const isActionsDisabled =
    loadingApprove || loadingReject || loadingUpdateWorkOrder;

  if (!isEditing) {
    return (
      <StaticView
        selectedPreflight={selectedPreflight}
        onEdit={() => setIsEditing(true)}
        isActionsDisabled={isActionsDisabled}
        loadingApprove={loadingApprove}
        loadingReject={loadingReject}
        onApprove={() => handleApprove(selectedPreflight.id)}
        onReject={(reason) => handleReject(selectedPreflight.id, { reason })}
      />
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box display="flex" gap="16px" mb="8px">
          <EditButton onClick={resetForm} disabled={isActionsDisabled}>
            {IconEditNoteMS}
            {Constants.cancelEdit}
          </EditButton>
          <SaveButton
            variant="contained"
            onClick={handleSave}
            disabled={isActionsDisabled}
          >
            {loadingUpdateWorkOrder ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              Constants.confirmChanges
            )}
          </SaveButton>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <SectionTitle>{Constants.requester}</SectionTitle>
      </Grid>
      <Grid item xs={4}>
        <CustomTextField
          id="requester-name"
          label={Constants.requesterName}
          value={requesterName}
          setValue={setRequesterName}
        />
      </Grid>
      <Grid item xs={4}>
        <CustomTextField
          id="phone"
          label={Constants.phone}
          value={requesterPhone}
          setValue={setRequesterPhone}
        />
      </Grid>
      <Grid item xs={4}>
        <CustomTextField
          id="email"
          label={Constants.email}
          value={requesterEmail}
          setValue={setRequesterEmail}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ margin: '8px 0' }} />
      </Grid>
      <Grid item xs={12}>
        <SectionTitle>{Constants.purpose}</SectionTitle>
      </Grid>
      <Grid item xs={4}>
        <SelectTextFieldString
          id="objective"
          label={Constants.goal}
          value={objective ?? ''}
          setValue={(v) => setObjective(v as typeof objective)}
          selectOptions={selectPreFlightObjective()}
        />
      </Grid>
      <Grid item xs={4}>
        <SelectTextFieldString
          id="property-type"
          label={Constants.propertyType}
          value={propertyType}
          setValue={(v) => setPropertyType(v as typeof propertyType)}
          selectOptions={selectPreFlightPropertyType()}
        />
      </Grid>
      <Grid item xs={4}>
        <SelectTextFieldString
          id="area-type"
          label={Constants.concept}
          value={areaType}
          setValue={(v) => setAreaType(v as typeof areaType)}
          selectOptions={selectPreFlightAreaType()}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ margin: '8px 0' }} />
      </Grid>
      <Grid item xs={12}>
        <SectionTitle>{Constants.report}</SectionTitle>
      </Grid>
      <Grid item xs={4}>
        <SelectTextFieldString
          id="modality"
          label={Constants.modality}
          value={modality}
          setValue={(v) => setModality(v as typeof modality)}
          selectOptions={selectPreFlightModality()}
        />
      </Grid>
      <Grid item xs={4}>
        <SelectTextFieldString
          id="method"
          label={Constants.calculationType}
          value={method}
          setValue={(v) => setMethod(v as typeof method)}
          selectOptions={selectPreFlightMethod()}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ margin: '8px 0' }} />
      </Grid>
      <Grid item xs={12}>
        <SectionTitle>{Constants.companion}</SectionTitle>
      </Grid>
      <Grid item xs={4}>
        <CustomTextField
          id="companion-name"
          label={Constants.name}
          value={companionName}
          setValue={setCompanionName}
        />
      </Grid>
      <Grid item xs={4}>
        <CustomTextField
          id="companion-email"
          label={Constants.email}
          value={companionEmail}
          setValue={setCompanionEmail}
        />
      </Grid>
      <Grid item xs={4}>
        <CustomTextField
          id="companion-phone"
          label={Constants.phone}
          value={companionPhone}
          setValue={setCompanionPhone}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ margin: '8px 0' }} />
      </Grid>
      <Grid item xs={12}>
        <SectionTitle>{Constants.address}</SectionTitle>
      </Grid>
      <Grid item xs={12}>
        <CustomTextField
          id="street"
          label={Constants.street}
          value={street}
          setValue={setStreet}
          onChange={() => setIsAddressEdited(true)}
        />
      </Grid>
      <Grid item xs={4}>
        <CustomTextField
          id="address-number"
          label={Constants.number}
          value={addressNumber}
          setValue={setAddressNumber}
          onChange={() => setIsAddressEdited(true)}
        />
      </Grid>
      <Grid item xs={4}>
        <CustomTextField
          id="complement"
          label={Constants.complement}
          value={complement}
          setValue={setComplement}
        />
      </Grid>
      <Grid item xs={4}>
        <CustomTextField
          id="zip-code"
          label={Constants.zipCode}
          value={zipCode}
          setValue={setZipCode}
        />
      </Grid>
      <Grid item xs={4}>
        <CustomTextField
          id="district"
          label={Constants.district}
          value={district}
          setValue={setDistrict}
          onChange={() => setIsAddressEdited(true)}
        />
      </Grid>
      <Grid item xs={4}>
        <CustomTextField
          id="city"
          label={Constants.city}
          value={city}
          setValue={setCity}
          onChange={() => setIsAddressEdited(true)}
        />
      </Grid>
      <Grid item xs={4}>
        <SelectTextField
          id="state"
          label={Constants.state}
          value={addressState}
          setValue={(v) => {
            setAddressState(v as number);
            setIsAddressEdited(true);
          }}
          selectOptions={selectRegistrationUf()}
          allSelectable={false}
        />
      </Grid>
      <Grid item xs={12}>
        <Box p="8px 24px">
          <StandardMap
            searchMap={searchMap}
            address={`${street} ${addressNumber}`}
            district={district}
            city={city}
            uf={convertRegistrationUf(addressState)}
            pinPlace={pinPlace}
            setPinPlace={setPinPlace}
            showCoords
          />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <MapSearchButton
          cep={zipCode}
          address={street}
          addressNumber={addressNumber}
          district={district}
          city={city}
          uf={addressState}
          hasSearched={hasSearched}
          isAddressEdited={isAddressEdited}
          labels={{
            searchMap: Constants.searchMap,
            mapUpdated: Constants.mapUpdated,
            fieldsIncomplete: Constants.fieldsIncomplete,
          }}
          onClick={() => {
            setSearchMap(!searchMap);
            setIsAddressEdited(false);
            setHasSearched(true);
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ margin: '8px 0' }} />
      </Grid>
      <Grid item xs={12}>
        <SectionTitle>{Constants.registration}</SectionTitle>
      </Grid>
      <Grid item xs={4}>
        <CustomTextField
          id="deed-number"
          label={Constants.registrationNumber}
          value={deedNumber}
          setValue={setDeedNumber}
        />
      </Grid>
      <Grid item xs={12}>
        <DeedUpload
          preFlightId={selectedPreflight.id}
          matriculaDoc={matriculaDoc}
          onUploadSuccess={onDeedUpload}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ margin: '8px 0' }} />
      </Grid>
      <Grid item xs={12}>
        <SectionTitle>{Constants.artRrt}</SectionTitle>
      </Grid>
      <Grid item xs={4}>
        <SelectTextFieldString
          id="art-rrt"
          label={Constants.artModality}
          value={artRrt}
          setValue={(v) => setArtRrt(v as typeof artRrt)}
          selectOptions={selectPreFlightArtRrt()}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ margin: '8px 0' }} />
      </Grid>
      <Grid item xs={12}>
        <SectionTitle>{Constants.complementaryServices}</SectionTitle>
      </Grid>
      <Grid item xs={12}>
        <Box display="flex" gap={4}>
          <CustomFormControl
            label={Constants.technicalReview}
            isChecked={technicalReview}
            setIsChecked={setTechnicalReview}
          />
          <CustomFormControl
            label={Constants.newRegistration}
            isChecked={updatedDeed}
            setIsChecked={setUpdatedDeed}
          />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ margin: '8px 0' }} />
      </Grid>
      <Grid item xs={12}>
        <SectionTitle>{Constants.documents}</SectionTitle>
      </Grid>
      <Grid item xs={12}>
        <DocumentBox sx={{ marginTop: '0' }}>
          <CustomTextField
            id="file-description"
            label={Constants.descriptionLabel}
            value={description}
            setValue={setDescription}
            maxLength={60}
          />
          <InputBox>
            <CustomTextField
              disabled
              id="file-name"
              label={Constants.fileLabel}
              value={fileName}
              setValue={setFileName}
            />
            <label htmlFor="uploadAttachFiles">
              <input
                accept="image/*,application/pdf"
                id="uploadAttachFiles"
                type="file"
                style={{ display: 'none' }}
                onChange={async (e: ChangeEvent<HTMLInputElement>) =>
                  handleFileUpload(e)
                }
              />
              <AttachFileButon component="span">
                {Constants.selectFile}
              </AttachFileButon>
            </label>
          </InputBox>
          <SubmitFileBox>
            <CancelFileButton onClick={handleReset}>
              {Constants.cancel}
            </CancelFileButton>
            <SubmitFileButton onClick={handleSubmitFile}>
              {Constants.add}
            </SubmitFileButton>
          </SubmitFileBox>
          <Divider sx={{ margin: '8px 0' }} />
          <AttachmentsBox>
            <DialogTitle my={3}>
              {IconFolderOpenMS}
              {Constants.files}
            </DialogTitle>
            {selectedPreflight.documents.length === 0 && (
              <Typography>{Constants.noAttachments}</Typography>
            )}
            <FileList>
              {selectedPreflight.documents.map((file) => (
                <FileContainer container key={file.download_url}>
                  <Box>
                    <FileNameTypography>
                      {IconAttachFileMS}
                      {file.original_filename}
                    </FileNameTypography>
                  </Box>
                  <Box>
                    {file.source === DocumentSource.REVIEWER && (
                      <StyledIcon
                        onClick={() => handleDeleteFile(file.reference)}
                      >
                        {IconDeleteMS}
                      </StyledIcon>
                    )}
                    <StyledIcon onClick={() => downloadFile(file.download_url)}>
                      {IconDownloadMS}
                    </StyledIcon>
                  </Box>
                </FileContainer>
              ))}
            </FileList>
          </AttachmentsBox>
        </DocumentBox>
      </Grid>
    </Grid>
  );
}
